<div class="sabu-modal">
  <div class="sabu-modal__header">
    <div class="sabu-modal__title-group">
      <h4 class="sabu-modal__title">Bulk Upload RDMS Recipes</h4>
      <p class="sabu-modal__subtitle">{{ subtitle }}</p>
    </div>
    <button type="button" class="sabu-modal__close-btn" (click)="activeModal.dismiss('Cross click')">
      <i data-lucide="x"></i>
    </button>
  </div>

  <div class="sabu-modal__body" *ngIf="step === 'upload'">
    <div class="sabu-upload-panel" *ngIf="!loadingLookups; else loadingTpl">
      <div class="sabu-steps">
        <div class="sabu-step">
          <span class="sabu-step__num">1</span>
          <div class="sabu-step__body">
            <h5 class="sabu-step__title">Download template</h5>
            <p class="sabu-step__text">Download CSV or Excel template with all Configure Row columns (name + Ids). Full 237-field exports also work.</p>
          </div>
        </div>
        <div class="sabu-step">
          <span class="sabu-step__num">2</span>
          <div class="sabu-step__body">
            <h5 class="sabu-step__title">Upload CSV or Excel</h5>
            <p class="sabu-step__text">Select your .csv or .xlsx file to open the preview table (max 5000 rows).</p>
          </div>
        </div>
        <div class="sabu-step">
          <span class="sabu-step__num">3</span>
          <div class="sabu-step__body">
            <h5 class="sabu-step__title">Configure each row (optional)</h5>
            <p class="sabu-step__text">Use the gear icon to set classification, ingredients, storage, and status when needed. Master columns from the file auto-fill when they match.</p>
          </div>
        </div>
      </div>

      <div class="sabu-actions-row">
        <div class="sabu-download-group" role="group" aria-label="Download template">
          <button type="button" class="btn btn--outline" (click)="downloadTemplate('csv')">
            <i data-lucide="download"></i>
            Download CSV
          </button>
          <button type="button" class="btn btn--outline" (click)="downloadTemplate('xlsx')">
            <i data-lucide="file-spreadsheet"></i>
            Download Excel
          </button>
        </div>
        <label class="btn btn--primary sabu-file-label">
          <i data-lucide="upload"></i>
          Select CSV / Excel
          <input
            type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            class="d-none"
            (change)="onFileSelected($event)"
            [disabled]="parsing"
          />
        </label>
      </div>

      <p class="sabu-hint" *ngIf="fileName">Selected: {{ fileName }}</p>
      <p class="sabu-error" *ngIf="fileError">{{ fileError }}</p>
      <p class="sabu-hint" *ngIf="parsing">Parsing file…</p>
      <p class="sabu-hint" *ngIf="enrichingIngredients">{{ enrichProgress || 'Fast-matching unique ingredients…' }}</p>

      <div class="sabu-help">
        <div class="sabu-help__grid">
          <div>
            <h6 class="sabu-help__heading">Accepted columns</h6>
            <p>Full 237-field Excel/CSV headers are accepted. Master fields accept <strong>ID or name</strong> (or mix in one cell). Matching values auto-fill Configure Row. <strong>No fields are required</strong> — empty Name imports as Untitled. Maximum <strong>5000</strong> rows per upload.</p>
          </div>
          <div>
            <h6 class="sabu-help__heading">Configure in preview</h6>
            <p>Image (optional), Health Color Code, Meal Type, Source, classifications, geo tags, allergens, storage/reheat dropdowns, ingredients, Recipe UOM, and status.</p>
          </div>
        </div>
        <p class="sabu-help__note">Default status is <strong>Draft</strong>. You can upload a thumbnail image per row in the preview table.</p>
      </div>
    </div>
    <ng-template #loadingTpl>
      <div class="sabu-loading">
        <div class="sabu-loading__spinner"></div>
        <p class="sabu-hint">Loading master data for dropdowns…</p>
      </div>
    </ng-template>
  </div>

  <div class="sabu-modal__body" *ngIf="step === 'preview'">
    <p class="sabu-hint" *ngIf="enrichingIngredients" style="margin-bottom: 10px;">
      {{ enrichProgress || 'Fast-matching unique ingredients (FSSAI tags calculate when you open a row)…' }}
    </p>
    <div class="sabu-toolbar">
      <div class="sabu-summary">
        <span class="sabu-badge sabu-badge--total">Total: {{ totalCount }}</span>
        <span class="sabu-badge sabu-badge--valid">Valid: {{ validCount }}</span>
        <span class="sabu-badge sabu-badge--error">Errors: {{ errorCount }}</span>
      </div>
      <div class="sabu-global-status">
        <label>Default status for all rows</label>
        <ng-select class="sabu-status-select" [items]="statusOptions" bindLabel="label" bindValue="value"
          [(ngModel)]="globalStatus" (ngModelChange)="onGlobalStatusChange()"
          [clearable]="false" appendTo="body"></ng-select>
      </div>
    </div>

    <div class="sabu-table-container">
      <table class="sabu-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Image</th>
            <th>Recipe Name</th>
            <th>Serves</th>
            <th>Status</th>
            <th>Configured</th>
            <th>Validation</th>
            <th *ngIf="errorCount > 0">Error</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of parseResult?.rows; let i = index" [class.sabu-row--error]="!row.valid">
            <td>{{ row.rowNumber }}</td>
            <td class="sabu-image-cell">
              <div class="sabu-thumb" *ngIf="row.selections.thumbnailImagePath; else noThumb">
                <img [src]="getRowThumbnailUrl(row)" alt="Recipe thumbnail" />
                <button type="button" class="sabu-thumb__remove" title="Remove image"
                  (click)="removeRowImage(row)" [disabled]="row.selections.thumbnailUploading">
                  <i data-lucide="x"></i>
                </button>
              </div>
              <ng-template #noThumb>
                <label class="sabu-thumb-upload" [class.sabu-thumb-upload--busy]="row.selections.thumbnailUploading">
                  <i data-lucide="image" *ngIf="!row.selections.thumbnailUploading"></i>
                  <span *ngIf="row.selections.thumbnailUploading">…</span>
                  <span *ngIf="!row.selections.thumbnailUploading">Add</span>
                  <input type="file" accept="image/png,image/jpeg,image/webp" class="d-none"
                    (change)="onRowImageSelected($event, row)"
                    [disabled]="row.selections.thumbnailUploading" />
                </label>
              </ng-template>
            </td>
            <td class="sabu-text-cell">{{ row.display.RecipeName || '—' }}</td>
            <td class="sabu-text-cell">{{ row.display.NumberOfServes || '—' }}</td>
            <td class="sabu-select-cell">
              <ng-select class="sabu-ng-select" [items]="statusOptions" bindLabel="label" bindValue="value"
                [(ngModel)]="row.selections.status" (ngModelChange)="onRowStatusChange(row)"
                [clearable]="false" appendTo="body"></ng-select>
            </td>
            <td>
              <span class="sabu-pill" [class.sabu-pill--yes]="row.configured" [class.sabu-pill--no]="!row.configured">
                {{ row.configured ? 'Yes' : 'No' }}
              </span>
            </td>
            <td>
              <span class="sabu-status" [class.sabu-status--valid]="row.valid" [class.sabu-status--error]="!row.valid">
                {{ row.valid ? 'Valid' : 'Error' }}
              </span>
            </td>
            <td class="sabu-error-cell" *ngIf="errorCount > 0">{{ row.error || '—' }}</td>
            <td class="sabu-action-cell">
              <button type="button" class="sabu-config-btn" title="Configure dropdowns" (click)="openRowEditor(row)">
                <i data-lucide="settings"></i>
              </button>
              <button type="button" class="sabu-delete-btn" title="Delete row" (click)="deleteRow(i)">
                <i data-lucide="trash-2"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div class="sabu-modal__footer">
    <button type="button" class="btn btn--outline" (click)="activeModal.dismiss('Cancel click')">Cancel</button>
    <button type="button" class="btn btn--outline" *ngIf="step === 'preview'" (click)="backToUpload()">Back</button>
    <button type="button" class="btn btn--primary" *ngIf="step === 'preview'"
      [disabled]="validCount === 0 || enrichingIngredients" (click)="confirmImport()">
      <i data-lucide="check"></i>
      Confirm Import ({{ validCount }})
    </button>
  </div>
</div>

<!-- Assign Maker & Checker / Checker-only dialog -->
<ng-template #assignModalTpl let-modal>
  <div class="sabu-assign">
    <div class="sabu-assign__header">
      <div class="sabu-assign__header-left">
        <div class="sabu-assign__icon">
          <i data-lucide="user-plus"></i>
        </div>
        <div>
          <h5 class="sabu-assign__title">
            {{ assignMode === 'checker-only'
              ? (assignDialogIsBulk ? 'Bulk Assign Checker' : 'Assign Checker')
              : (assignDialogIsBulk ? 'Bulk Assign Recipes' : 'Assign Maker & Checker') }}
          </h5>
          <p class="sabu-assign__subtitle">
            {{ assignMode === 'checker-only'
              ? 'Select a Checker for Super Admin assignment.'
              : 'Maker and Checker are assigned together.' }}
          </p>
        </div>
      </div>
      <button type="button" class="sabu-assign__close" (click)="closeAssignDialog(modal)">&times;</button>
    </div>

    <div class="sabu-assign__body">
      <div *ngIf="!assignDialogIsBulk && assignTargetRecipe" class="sabu-assign__recipe">
        <div class="sabu-assign__recipe-thumb">
          <img *ngIf="getRowThumbnailUrl(assignTargetRecipe)" [src]="getRowThumbnailUrl(assignTargetRecipe)!" alt="" />
          <i *ngIf="!getRowThumbnailUrl(assignTargetRecipe)" data-lucide="chef-hat"></i>
        </div>
        <div>
          <div class="sabu-assign__recipe-name">{{ assignTargetRecipe.display.RecipeName || 'Untitled' }}</div>
          <div class="sabu-assign__recipe-meta">Row #{{ assignTargetRecipe.rowNumber }}</div>
        </div>
      </div>

      <div *ngIf="assignDialogIsBulk" class="sabu-assign__bulk-banner">
        <div class="sabu-assign__bulk-label">Bulk Assignment</div>
        <div class="sabu-assign__bulk-count">{{ getAssignTargetCount() }} recipes selected</div>
      </div>

      <div class="sabu-assign__info" *ngIf="assignMode === 'maker-and-checker'">
        Item goes to Maker first; after Maker submits it routes automatically to the selected Checker.
      </div>
      <div class="sabu-assign__info sabu-assign__info--checker" *ngIf="assignMode === 'checker-only'">
        Recipe will be assigned directly to the selected Checker.
      </div>

      <div class="sabu-assign__field" *ngIf="assignMode === 'maker-and-checker'">
        <label>Select Maker <span class="text-danger">*</span></label>
        <ng-select
          [items]="makerList"
          bindValue="email"
          [(ngModel)]="selectedMakerEmail"
          placeholder="Select maker"
          [searchable]="true"
          [clearable]="true"
          [loading]="isMakerLoading"
          appendTo="body"
        >
          <ng-template ng-label-tmp let-item="item">
            {{ item.firstName || item.name }} {{ item.lastName || '' }}<ng-container *ngIf="item.email"> ({{ item.email }})</ng-container>
          </ng-template>
          <ng-template ng-option-tmp let-item="item">
            {{ item.firstName || item.name }} {{ item.lastName || '' }}<ng-container *ngIf="item.email"> ({{ item.email }})</ng-container>
          </ng-template>
        </ng-select>
      </div>

      <div class="sabu-assign__field">
        <label>Select Checker <span class="text-danger">*</span></label>
        <ng-select
          [items]="checkerList"
          bindValue="email"
          [(ngModel)]="selectedCheckerEmail"
          placeholder="Select checker"
          [searchable]="true"
          [clearable]="true"
          [loading]="isCheckerLoading"
          appendTo="body"
        >
          <ng-template ng-label-tmp let-item="item">
            {{ item.firstName || item.name }} {{ item.lastName || '' }}<ng-container *ngIf="item.email"> ({{ item.email }})</ng-container>
          </ng-template>
          <ng-template ng-option-tmp let-item="item">
            {{ item.firstName || item.name }} {{ item.lastName || '' }}<ng-container *ngIf="item.email"> ({{ item.email }})</ng-container>
          </ng-template>
        </ng-select>
      </div>

      <div class="sabu-assign__row">
        <div class="sabu-assign__field">
          <label>Due Date (Optional)</label>
          <input type="date" class="sabu-assign__date" [(ngModel)]="selectedDueDate" />
        </div>
        <div class="sabu-assign__field">
          <label>Priority</label>
          <ng-select
            [items]="assignmentPriorities"
            [(ngModel)]="selectedPriority"
            [searchable]="false"
            [clearable]="false"
            appendTo="body"
          ></ng-select>
        </div>
      </div>

      <div class="sabu-assign__field">
        <label>Notes</label>
        <textarea rows="3" [(ngModel)]="selectedNotes" placeholder="Add optional instructions..."></textarea>
      </div>
    </div>

    <div class="sabu-assign__footer">
      <button type="button" class="btn btn--outline" (click)="closeAssignDialog(modal)">Cancel</button>
      <button
        type="button"
        class="btn btn--primary"
        (click)="confirmAssignment(modal)"
        [disabled]="assignMode === 'maker-and-checker'
          ? (!selectedMakerEmail || !selectedCheckerEmail)
          : !selectedCheckerEmail"
      >
        <i data-lucide="check"></i>
        Confirm & Assign
      </button>
    </div>
  </div>
</ng-template>
